/* Radiate Your Light Creations — persistent support rail v3.0 */
(() => {
  const root = document.getElementById('ryl-accessibility-system');
  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  const KEY = 'rylAccessibilityPrefsV2';
  const OLD_KEY = 'rylAccessibilityPrefsV1';
  const defaults = {
    visualNav: false,
    textScale: 100,
    moreSpace: false,
    highContrast: false,
    underlineLinks: false,
    readingWidth: false,
    reduceMotion: false,
    quietVisuals: false,
    strongFocus: false,
    soundOff: false,
    readRate: 0.95,
    soundVolume: 20
  };
  let prefs = { ...defaults };
  let openState = null;
  let lastFocus = null;
  let previousBodyOverflow = '';

  const triggers = {
    accessibility: root.querySelector('#ryl-accessibility-button'),
    navigation: root.querySelector('#ryl-navigation-button'),
    mindfulness: root.querySelector('#ryl-mindfulness-button')
  };
  const overlays = {
    accessibility: root.querySelector('#ryl-accessibility-overlay'),
    navigation: root.querySelector('#ryl-navigation-overlay'),
    mindfulness: root.querySelector('#ryl-mindfulness-overlay')
  };
  const panels = {
    accessibility: root.querySelector('#ryl-accessibility-panel'),
    navigation: root.querySelector('#ryl-navigation-panel'),
    mindfulness: root.querySelector('#ryl-mindfulness-panel')
  };

  const closeButtons = root.querySelectorAll('[data-ryl-panel-close]');
  const resetButton = root.querySelector('[data-ryl-a11y-reset]');
  const settingButtons = root.querySelectorAll('[data-ryl-setting]');
  const textScaleButtons = root.querySelectorAll('[data-ryl-text-scale]');
  const readStart = root.querySelector('[data-ryl-read-start]');
  const readPause = root.querySelector('[data-ryl-read-pause]');
  const readStop = root.querySelector('[data-ryl-read-stop]');
  const readStatus = root.querySelector('[data-ryl-read-status]');
  const readRate = root.querySelector('[data-ryl-read-rate]');
  const readRateOutput = root.querySelector('[data-ryl-read-rate-output]');
  const checkin = root.querySelector('#ryl-support-checkin');
  const checkinOpen = root.querySelector('[data-ryl-checkin-open]');
  const checkinResponse = root.querySelector('[data-ryl-checkin-response]');
  const soundSection = root.querySelector('#ryl-support-sound');
  const soundFocus = root.querySelector('[data-ryl-sound-focus]');
  const audio = root.querySelector('#ryl-support-audio');
  const trackButtons = root.querySelectorAll('[data-ryl-support-track]');
  const audioToggle = root.querySelector('[data-ryl-support-audio-toggle]');
  const audioStop = root.querySelector('[data-ryl-support-audio-stop]');
  const audioStatus = root.querySelector('[data-ryl-support-audio-status]');
  const volume = root.querySelector('[data-ryl-support-volume]');
  const volumeOutput = root.querySelector('[data-ryl-support-volume-output]');

  const speechSupported = 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
  let readingBlocks = [];
  let readingIndex = -1;
  let readingActive = false;
  let currentBlock = null;
  let selectedTrackLabel = '';

  function safeRead() {
    try {
      let parsed = JSON.parse(localStorage.getItem(KEY) || '{}');
      if (!parsed || typeof parsed !== 'object' || !Object.keys(parsed).length) {
        const old = JSON.parse(localStorage.getItem(OLD_KEY) || '{}');
        if (old && typeof old === 'object') {
          parsed = { ...old };
          if (old.bigText) parsed.textScale = 125;
        }
      }
      prefs = { ...defaults, ...parsed };
      prefs.textScale = [100,125,150,175,200].includes(Number(prefs.textScale)) ? Number(prefs.textScale) : 100;
      prefs.readRate = Math.min(1.3, Math.max(.7, Number(prefs.readRate) || .95));
      prefs.soundVolume = Math.min(100, Math.max(0, Number(prefs.soundVolume) || 20));
    } catch (_) {}
  }

  function safeWrite() {
    try { localStorage.setItem(KEY, JSON.stringify(prefs)); } catch (_) {}
  }

  function dispatchPreferences() {
    document.dispatchEvent(new CustomEvent('ryl:accessibility-change', { detail: { preferences: { ...prefs, bigText: prefs.textScale > 100 } } }));
  }

  function clearWordHighlight() {
    try { if (window.CSS && CSS.highlights) CSS.highlights.delete('ryl-spoken-word'); } catch (_) {}
  }

  function setReadStatus(message) { if (readStatus) readStatus.textContent = message; }
  function setAudioStatus(message) { if (audioStatus) audioStatus.textContent = message; }

  function stopReading(announce = true) {
    readingActive = false;
    readingIndex = -1;
    if (speechSupported) { try { window.speechSynthesis.cancel(); } catch (_) {} }
    if (currentBlock) currentBlock.classList.remove('ryl-a11y-reading-block');
    currentBlock = null;
    clearWordHighlight();
    if (readPause) { readPause.disabled = true; readPause.textContent = 'Pause'; }
    if (readStop) readStop.disabled = true;
    if (announce) setReadStatus('Reading is off.');
  }

  function stopBackgroundAudio(resetTrack = false) {
    if (!audio) return;
    try { audio.pause(); audio.currentTime = 0; } catch (_) {}
    if (audioToggle) { audioToggle.disabled = !audio.src; audioToggle.textContent = 'Play'; }
    if (audioStop) audioStop.disabled = !audio.src;
    trackButtons.forEach((button) => button.setAttribute('aria-pressed', 'false'));
    if (resetTrack) {
      audio.removeAttribute('src');
      try { audio.load(); } catch (_) {}
      selectedTrackLabel = '';
      if (audioToggle) audioToggle.disabled = true;
      if (audioStop) audioStop.disabled = true;
      setAudioStatus('No background sound selected.');
    } else if (selectedTrackLabel) {
      setAudioStatus(`${selectedTrackLabel} is paused.`);
    }
  }

  function stopSiteSound() {
    stopReading(false);
    stopBackgroundAudio(false);
    document.querySelectorAll('audio').forEach((media) => { if (media !== audio) { try { media.pause(); } catch (_) {} } });
    document.querySelectorAll('video').forEach((media) => { try { media.muted = true; } catch (_) {} });
  }

  function pauseDecorativeMotion() {
    document.querySelectorAll('video[autoplay]').forEach((video) => { try { video.pause(); } catch (_) {} });
  }

  function applyClasses() {
    const html = document.documentElement;
    const classMap = {
      visualNav: 'ryl-a11y-visual-nav',
      moreSpace: 'ryl-a11y-more-space',
      highContrast: 'ryl-a11y-high-contrast',
      underlineLinks: 'ryl-a11y-underline-links',
      readingWidth: 'ryl-a11y-reading-width',
      reduceMotion: 'ryl-a11y-reduce-motion',
      quietVisuals: 'ryl-a11y-quiet-visuals',
      strongFocus: 'ryl-a11y-strong-focus',
      soundOff: 'ryl-a11y-sound-off'
    };
    Object.entries(classMap).forEach(([key,className]) => html.classList.toggle(className, Boolean(prefs[key])));
    [125,150,175,200].forEach((scale) => html.classList.toggle(`ryl-a11y-text-${scale}`, prefs.textScale === scale));

    settingButtons.forEach((button) => {
      const key = button.dataset.rylSetting;
      button.setAttribute('aria-pressed', String(Boolean(prefs[key])));
    });
    textScaleButtons.forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.rylTextScale) === prefs.textScale)));
    if (readRate) readRate.value = String(prefs.readRate);
    if (readRateOutput) readRateOutput.value = `${prefs.readRate.toFixed(2).replace(/0$/, '')}×`;
    if (volume) volume.value = String(prefs.soundVolume);
    if (volumeOutput) volumeOutput.value = `${prefs.soundVolume}%`;
    if (audio) audio.volume = prefs.soundVolume / 100;

    if (prefs.soundOff) stopSiteSound();
    if (prefs.reduceMotion || prefs.quietVisuals || matchMedia('(prefers-reduced-motion: reduce)').matches) pauseDecorativeMotion();
  }

  function closeLegacyMindfulIfOpen() {
    const mindfulOverlay = document.getElementById('ryl-mindful-overlay');
    if (!mindfulOverlay || mindfulOverlay.hidden) return;
    const close = mindfulOverlay.querySelector('[data-close-mindful], .ryl-close');
    if (close) close.click();
  }

  function closePanel(restoreFocus = true) {
    if (!openState) return;
    const overlay = overlays[openState];
    const trigger = triggers[openState];
    if (overlay) overlay.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = previousBodyOverflow;
    const focusTarget = lastFocus;
    openState = null;
    if (restoreFocus) {
      if (focusTarget && document.contains(focusTarget)) focusTarget.focus();
      else if (trigger) trigger.focus();
    }
  }

  function openPanel(name) {
    if (!overlays[name] || !panels[name] || !triggers[name]) return;
    closeLegacyMindfulIfOpen();
    if (openState) closePanel(false);
    lastFocus = document.activeElement;
    openState = name;
    previousBodyOverflow = document.body.style.overflow;
    overlays[name].hidden = false;
    triggers[name].setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => panels[name].focus());
  }

  function panelFocusable(panel) {
    return [...panel.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((el) => !el.hidden && el.offsetParent !== null);
  }

  function trapFocus(event, panel) {
    if (event.key === 'Escape') { event.preventDefault(); closePanel(); return; }
    if (event.key !== 'Tab') return;
    const items = panelFocusable(panel);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function isVisible(element) {
    if (!element) return false;
    const style = getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  }

  function updateCommerceOffset() {
    let offset = 0;
    const sticky = document.querySelector('.sticky-add-to-cart__bar[data-stuck="true"]');
    if (isVisible(sticky)) {
      const rect = sticky.getBoundingClientRect();
      if (rect.bottom >= window.innerHeight - 4) offset = Math.ceil(rect.height + 12);
    }
    document.documentElement.style.setProperty('--ryl-commerce-bottom-offset', `${offset}px`);
  }

  function readingCandidates() {
    const main = document.getElementById('MainContent') || document.querySelector('main');
    if (!main) return [];
    const selector = 'h1,h2,h3,h4,h5,h6,p,li,figcaption,summary,legend,blockquote';
    return [...main.querySelectorAll(selector)].filter((el) => {
      if (!isVisible(el)) return false;
      if (el.closest('[hidden], [aria-hidden="true"], script, style, noscript, template, #ryl-accessibility-system')) return false;
      return (el.textContent || '').replace(/\s+/g, ' ').trim().length > 1;
    });
  }

  function textPositionToRange(element, start, length) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode(node) { return node.nodeValue && node.nodeValue.length ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT; }
    });
    let node, offset = 0, startNode = null, startOffset = 0, endNode = null, endOffset = 0;
    const wantedEnd = start + Math.max(1, length);
    while ((node = walker.nextNode())) {
      const next = offset + node.nodeValue.length;
      if (!startNode && start >= offset && start <= next) { startNode = node; startOffset = Math.max(0, Math.min(node.nodeValue.length, start - offset)); }
      if (wantedEnd >= offset && wantedEnd <= next) { endNode = node; endOffset = Math.max(0, Math.min(node.nodeValue.length, wantedEnd - offset)); break; }
      offset = next;
    }
    if (!startNode) return null;
    if (!endNode) { endNode = startNode; endOffset = Math.min(startNode.nodeValue.length, startOffset + Math.max(1,length)); }
    try { const range = new Range(); range.setStart(startNode,startOffset); range.setEnd(endNode,endOffset); return range; } catch (_) { return null; }
  }

  function highlightBoundary(element, text, event) {
    if (!window.CSS || !CSS.highlights || typeof Highlight === 'undefined') return;
    const start = Number(event.charIndex || 0);
    let length = Number(event.charLength || 0);
    if (!length) {
      const tail = text.slice(start);
      const match = tail.match(/^\s*([^\s.,;:!?()[\]{}]+)/);
      if (match) {
        const leading = match[0].indexOf(match[1]);
        const range = textPositionToRange(element,start + leading,match[1].length);
        if (range) CSS.highlights.set('ryl-spoken-word', new Highlight(range));
        return;
      }
      length = 1;
    }
    const range = textPositionToRange(element,start,length);
    if (range) CSS.highlights.set('ryl-spoken-word', new Highlight(range));
  }

  function speakNext() {
    if (!readingActive) return;
    readingIndex += 1;
    if (readingIndex >= readingBlocks.length) { stopReading(false); setReadStatus('Finished reading this page.'); return; }
    if (currentBlock) currentBlock.classList.remove('ryl-a11y-reading-block');
    clearWordHighlight();
    currentBlock = readingBlocks[readingIndex];
    currentBlock.classList.add('ryl-a11y-reading-block');
    currentBlock.scrollIntoView({ block:'center', behavior:(prefs.reduceMotion || matchMedia('(prefers-reduced-motion: reduce)').matches) ? 'auto' : 'smooth' });
    const text = (currentBlock.textContent || '').replace(/\s+/g,' ').trim();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = document.documentElement.lang || navigator.language || 'en-US';
    utterance.rate = prefs.readRate;
    utterance.onboundary = (event) => highlightBoundary(currentBlock,text,event);
    utterance.onend = () => { if (readingActive) speakNext(); };
    utterance.onerror = (event) => { if (event.error === 'canceled' || event.error === 'interrupted') return; stopReading(false); setReadStatus('Page reading could not continue in this browser.'); };
    setReadStatus(`Reading section ${readingIndex + 1} of ${readingBlocks.length}.`);
    window.speechSynthesis.speak(utterance);
  }

  function startReading() {
    if (!speechSupported) { setReadStatus('Page reading is not available in this browser.'); return; }
    if (prefs.soundOff) { setReadStatus('Sound Off is on. Turn it off before starting page reading.'); return; }
    stopBackgroundAudio(false);
    stopReading(false);
    readingBlocks = readingCandidates();
    if (!readingBlocks.length) { setReadStatus('No readable page text was found.'); return; }
    readingActive = true; readingIndex = -1;
    if (readPause) { readPause.disabled = false; readPause.textContent = 'Pause'; }
    if (readStop) readStop.disabled = false;
    closePanel(true);
    speakNext();
  }

  function togglePause() {
    if (!speechSupported || !readingActive) return;
    if (window.speechSynthesis.paused) { window.speechSynthesis.resume(); readPause.textContent = 'Pause'; setReadStatus(`Reading section ${readingIndex + 1} of ${readingBlocks.length}.`); }
    else { window.speechSynthesis.pause(); readPause.textContent = 'Resume'; setReadStatus('Reading paused.'); }
  }

  function playTrack(button) {
    if (!audio) return;
    if (prefs.soundOff) { setAudioStatus('Sound Off is on. Turn it off in Accessibility before playing background sound.'); return; }
    stopReading(false);
    const src = button.dataset.rylSupportTrack;
    const label = button.dataset.rylSupportTrackLabel || 'Background sound';
    if (!src) return;
    const changed = audio.getAttribute('src') !== src;
    if (changed) { audio.src = src; try { audio.load(); } catch (_) {} }
    selectedTrackLabel = label;
    audio.volume = prefs.soundVolume / 100;
    trackButtons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
    audio.play().then(() => {
      if (audioToggle) { audioToggle.disabled = false; audioToggle.textContent = 'Pause'; }
      if (audioStop) audioStop.disabled = false;
      setAudioStatus(`${label} is playing at ${prefs.soundVolume}% volume.`);
    }).catch(() => setAudioStatus(`${label} could not start in this browser.`));
  }

  function toggleAudio() {
    if (!audio || !audio.src) return;
    if (prefs.soundOff) { setAudioStatus('Sound Off is on.'); return; }
    if (audio.paused) {
      audio.play().then(() => { audioToggle.textContent = 'Pause'; setAudioStatus(`${selectedTrackLabel} is playing at ${prefs.soundVolume}% volume.`); }).catch(() => setAudioStatus('The background sound could not start.'));
    } else {
      audio.pause(); audioToggle.textContent = 'Play'; setAudioStatus(`${selectedTrackLabel} is paused.`);
    }
  }

  function renderCheckin(kind) {
    const messages = {
      overloaded: '<strong>Overloaded:</strong> fewer inputs may help. You could turn on Quiet Visuals, turn sound off, close anything you do not need, or choose a short grounding Mindful Moment.',
      low: '<strong>Low energy:</strong> make the next step smaller. You might choose Bigger Text, a quieter view, a short pause, or the Healing Barn without needing to complete anything.',
      steady: '<strong>Fairly steady:</strong> you can keep going as you are, or use this as a chance to choose one intentional next step instead of opening everything at once.',
      unsure: '<strong>Not sure:</strong> you do not have to figure it out first. Try one small support—Quiet Visuals, a Mindful Moment, or a Healing Barn break—and change course whenever you want.'
    };
    if (checkinResponse) checkinResponse.innerHTML = messages[kind] || messages.unsure;
  }

  Object.entries(triggers).forEach(([name,button]) => { if (button) button.addEventListener('click', () => openPanel(name)); });
  closeButtons.forEach((button) => button.addEventListener('click', () => closePanel()));
  Object.entries(overlays).forEach(([name,overlay]) => { if (overlay) overlay.addEventListener('click', (event) => { if (event.target === overlay) closePanel(); }); });
  Object.values(panels).forEach((panel) => { if (panel) panel.addEventListener('keydown', (event) => trapFocus(event,panel)); });

  settingButtons.forEach((button) => button.addEventListener('click', () => {
    const key = button.dataset.rylSetting;
    if (!(key in prefs)) return;
    prefs[key] = !prefs[key]; safeWrite(); applyClasses(); dispatchPreferences();
  }));

  textScaleButtons.forEach((button) => button.addEventListener('click', () => {
    prefs.textScale = Number(button.dataset.rylTextScale) || 100; safeWrite(); applyClasses(); dispatchPreferences();
  }));

  if (readRate) readRate.addEventListener('input', () => {
    prefs.readRate = Number(readRate.value) || .95;
    if (readRateOutput) readRateOutput.value = `${prefs.readRate.toFixed(2).replace(/0$/, '')}×`;
    safeWrite();
  });
  if (readStart) readStart.addEventListener('click', startReading);
  if (readPause) readPause.addEventListener('click', togglePause);
  if (readStop) readStop.addEventListener('click', () => stopReading(true));

  const navTop = root.querySelector('[data-ryl-nav-top]');
  if (navTop) navTop.addEventListener('click', () => {
    const behavior=(prefs.reduceMotion || matchMedia('(prefers-reduced-motion: reduce)').matches) ? 'auto' : 'smooth';
    closePanel(false);
    try {
      if (location.hash) history.replaceState(history.state,'',location.pathname + location.search);
    } catch (_) {}

    // Horizon uses .page-wrapper as the desktop scroll container (>= 990px).
    // Scrolling only window/document can therefore appear to do nothing in Shopify preview.
    const scrollPageToTop = (mode) => {
      const pageWrapper = document.querySelector('.page-wrapper');
      const docScroller = document.scrollingElement || document.documentElement;
      const targets = [pageWrapper, docScroller].filter(Boolean);

      targets.forEach((target) => {
        try { target.scrollTo({top:0,left:0,behavior:mode}); }
        catch (_) { target.scrollTop=0; target.scrollLeft=0; }
      });
      try { window.scrollTo({top:0,left:0,behavior:mode}); }
      catch (_) { window.scrollTo(0,0); }

      // Explicit root reset covers Safari and Shopify theme-preview edge cases.
      if (mode === 'auto') {
        document.documentElement.scrollTop=0;
        document.body.scrollTop=0;
        if (pageWrapper) pageWrapper.scrollTop=0;
      }
    };

    requestAnimationFrame(() => requestAnimationFrame(() => {
      scrollPageToTop(behavior);
      // Reassert after the drawer/overlay transition finishes so focus restoration
      // or Shopify preview UI cannot restore the previous position.
      setTimeout(() => {
        const pageWrapper = document.querySelector('.page-wrapper');
        const pageTop = pageWrapper ? pageWrapper.scrollTop : 0;
        const documentTop = (document.scrollingElement || document.documentElement).scrollTop;
        if (pageTop > 8 || documentTop > 8) scrollPageToTop('auto');
      }, 500);
    }));
  });
  const navExit = root.querySelector('[data-ryl-nav-exit]');
  if (navExit) navExit.addEventListener('click', () => {
    const home = root.dataset.rylHomeUrl || '/';
    closePanel(false);
    try {
      const referrer = document.referrer ? new URL(document.referrer) : null;
      if (history.length > 1 && referrer && referrer.origin === location.origin) history.back();
      else location.href = home;
    } catch (_) { location.href = home; }
  });


  root.querySelectorAll('.ryl-support-panel a[href]').forEach((link) => {
    link.addEventListener('click', () => closePanel(false));
  });

  const mindfulRail = root.querySelector('[data-ryl-open-mindful-rail]');
  if (mindfulRail) mindfulRail.addEventListener('click', () => {
    closePanel(true);
    requestAnimationFrame(() => {
      if (window.RYLRegulation && typeof window.RYLRegulation.open === 'function') window.RYLRegulation.open('anytime');
    });
  });

  if (checkinOpen && checkin) checkinOpen.addEventListener('click', () => {
    const willOpen = checkin.hidden;
    checkin.hidden = !willOpen;
    checkinOpen.setAttribute('aria-expanded', String(willOpen));
    if (willOpen) requestAnimationFrame(() => checkin.scrollIntoView({ block:'nearest', behavior:prefs.reduceMotion ? 'auto' : 'smooth' }));
  });
  root.querySelectorAll('[data-ryl-checkin]').forEach((button) => button.addEventListener('click', () => renderCheckin(button.dataset.rylCheckin)));
  if (soundFocus && soundSection) soundFocus.addEventListener('click', () => soundSection.scrollIntoView({ block:'start', behavior:prefs.reduceMotion ? 'auto' : 'smooth' }));
  trackButtons.forEach((button) => { button.setAttribute('aria-pressed','false'); button.addEventListener('click', () => playTrack(button)); });
  if (audioToggle) audioToggle.addEventListener('click', toggleAudio);
  if (audioStop) audioStop.addEventListener('click', () => stopBackgroundAudio(true));
  if (volume) volume.addEventListener('input', () => {
    prefs.soundVolume = Number(volume.value);
    if (audio) audio.volume = prefs.soundVolume / 100;
    if (volumeOutput) volumeOutput.value = `${prefs.soundVolume}%`;
    safeWrite();
    if (audio && !audio.paused && selectedTrackLabel) setAudioStatus(`${selectedTrackLabel} is playing at ${prefs.soundVolume}% volume.`);
  });

  resetButton?.addEventListener('click', () => {
    prefs = { ...defaults };
    try { localStorage.removeItem(KEY); localStorage.removeItem(OLD_KEY); } catch (_) {}
    stopReading(true); stopBackgroundAudio(true); applyClasses(); dispatchPreferences();
  });

  document.addEventListener('ryl:accessibility-set', (event) => {
    const requested = event.detail?.preferences;
    if (!requested || typeof requested !== 'object') return;
    let changed = false;
    Object.keys(defaults).forEach((key) => {
      if (key === 'textScale' || key === 'readRate' || key === 'soundVolume') return;
      if (typeof requested[key] !== 'boolean' || prefs[key] === requested[key]) return;
      prefs[key] = requested[key]; changed = true;
    });
    if (typeof requested.bigText === 'boolean') { const next = requested.bigText ? 125 : 100; if (prefs.textScale !== next) { prefs.textScale = next; changed = true; } }
    if (!changed) return;
    safeWrite(); applyClasses(); dispatchPreferences();
  });

  document.addEventListener('play', (event) => {
    if (!prefs.soundOff) return;
    const target = event.target;
    if (target instanceof HTMLMediaElement) { if (target.tagName === 'AUDIO') target.pause(); else target.muted = true; }
  }, true);

  const reduceMedia = matchMedia('(prefers-reduced-motion: reduce)');
  const onDeviceMotionChange = () => { if (reduceMedia.matches) pauseDecorativeMotion(); };
  if (reduceMedia.addEventListener) reduceMedia.addEventListener('change', onDeviceMotionChange); else if (reduceMedia.addListener) reduceMedia.addListener(onDeviceMotionChange);

  window.addEventListener('resize', updateCommerceOffset, { passive:true });
  document.addEventListener('scroll', updateCommerceOffset, { passive:true });
  window.addEventListener('pagehide', () => { stopReading(false); stopBackgroundAudio(false); });

  const stickyCommerceBar = document.querySelector('.sticky-add-to-cart__bar');
  if (stickyCommerceBar) {
    const observer = new MutationObserver(updateCommerceOffset);
    observer.observe(stickyCommerceBar,{ attributes:true, attributeFilter:['data-stuck','class','style'] });
    if ('ResizeObserver' in window) new ResizeObserver(updateCommerceOffset).observe(stickyCommerceBar);
  }

  if (!speechSupported && readStart) { readStart.disabled = true; readStart.setAttribute('aria-disabled','true'); setReadStatus('Page reading is not available in this browser.'); }

  updateCommerceOffset();
  safeRead();
  applyClasses();
})();
